import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SignUpTermsComponent } from '../sign-up-terms/sign-up-terms.component';
import { Router, RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SocialLoginComponent } from '@app/components/shared/social-login/social-login.component';
import { SignUpForm } from '@app/utils/interfaces/sign-up-form.interface';
import { ErrorTypes } from '@app/utils/types/error-types';
import { SignUpService } from '@app/services/sign-up/sign-up.service';
import { CustomValidators } from '@app/utils/validators/custom-validators';
import { ErrorModalComponent } from '@app/components/shared/error-modal/error-modal.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { PasswordHintsComponent } from '@app/components/shared/password-hints/password-hints.component';
import { SIGNUP_FORM_ERROR_MESSAGES } from '@app/utils/constants/error-messages.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { InputType } from '@app/utils/enums/input-type.enum';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { AuthService } from '@app/services/auth/auth.service';

@Component({
  selector: 'app-sign-up-form',
  templateUrl: './sign-up-form.component.html',
  styleUrls: ['./sign-up-form.component.scss'],
  imports: [
    NgClass,
    ReactiveFormsModule,
    SocialLoginComponent,
    TranslateModule,
    PasswordHintsComponent,
    IconComponent,
    RouterModule,
  ],

  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);
  private readonly signUpService = inject(SignUpService);
  private readonly authService = inject(AuthService);
  protected readonly inputType = InputType;
  protected readonly iconName = IconName;
  protected isPasswordVisible = false;
  protected isConfirmPasswordVisible = false;
  protected signUpForm!: FormGroup;
  public appRoutes: typeof AppRoutes = AppRoutes;

  public ngOnInit(): void {
    this.signUpForm = this.fb.group<SignUpForm>({
      firstName: this.fb.control('', [CustomValidators.name()]),
      lastName: this.fb.control('', [CustomValidators.name()]),
      email: this.fb.control('', [CustomValidators.email()]),
      password: this.fb.control('', [CustomValidators.password()]),
      confirmPassword: this.fb.control('', [
        Validators.required,
        CustomValidators.confirmPassword('password'),
      ]),
      termsAccepted: this.fb.control(false, [Validators.requiredTrue]),
      newsletter: this.fb.control(false),
    });
  }

  protected hasError(controlName: keyof SignUpForm, errorType?: ErrorTypes): boolean {
    const control = this.signUpForm.get(controlName);

    if (!control || !control.touched || !control.errors) {
      return false;
    }

    return errorType ? control.hasError(errorType) : true;
  }

  public getErrorMessage(controlName: keyof SignUpForm, errorType: ErrorTypes): string {
    return this.translate.instant(SIGNUP_FORM_ERROR_MESSAGES[controlName]?.[errorType] || '');
  }

  protected onSubmit(): void {
    if (this.signUpForm.invalid) {
      this.signUpForm.markAllAsTouched();
      return;
    }

    const { firstName, lastName, email, password, confirmPassword } = this.signUpForm.value;

    this.signUpService
      .signUpUser({ firstName, lastName, email, password, confirmPassword })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.authService.setSession(response.data);
          this.router.navigate(['/main']);
        },
        error: (error) => {
          this.dialogRef.open(ErrorModalComponent, {
            data: { message: error?.error?.message },
          });
        },
      });
  }

  protected onTermsAcceptModal() {
    const refDialog = this.dialogRef.open(SignUpTermsComponent, {
      data: { acceptTerms: false },
    });

    refDialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((accept: boolean) => {
        if (accept) {
          this.signUpForm.patchValue({ termsAccepted: true });
        }
      });
  }

  public togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  public toggleConfirmPasswordVisibility(): void {
    this.isConfirmPasswordVisible = !this.isConfirmPasswordVisible;
  }
}
